/**
 * Pure transitions for the bulk ROI selection set. No Recoil, no React.
 *
 * The selection is valid only when empty or of size >= 2; any transition that
 * would land on size 1 collapses to the empty set so the caller can fall
 * through to single-selection behavior.
 */

const EMPTY: ReadonlySet<number> = new Set();

/**
 * Toggle membership of `clicked` (ctrl/cmd-click semantics).
 *
 * The working set seeds from `current` when non-empty, otherwise from the
 * active ROI when one is present. A result smaller than two members collapses
 * to empty, signalling the caller to fall through to plain-click behavior.
 */
export function applyCtrlToggle(
    current: ReadonlySet<number>,
    activeRoiNumber: number,
    clicked: number
): ReadonlySet<number> {
    const working = new Set<number>(
        current.size > 0
            ? current
            : activeRoiNumber !== -1
              ? [activeRoiNumber]
              : []
    );

    if (working.has(clicked)) {
        working.delete(clicked);
    } else {
        working.add(clicked);
    }

    if (working.size < 2) {
        return EMPTY;
    }
    return working;
}

/**
 * Replace the selection with the inclusive range between `anchor` and
 * `clicked` over `visualOrder` (shift-click semantics). A null or absent anchor
 * falls back to `clicked`. A degenerate range of a single member collapses to
 * empty.
 */
export function applyShiftRange(
    visualOrder: readonly number[],
    anchor: number | null,
    clicked: number
): ReadonlySet<number> {
    const clickedIndex = visualOrder.indexOf(clicked);
    if (clickedIndex === -1) {
        return EMPTY;
    }

    const anchorIndex = anchor === null ? -1 : visualOrder.indexOf(anchor);
    const effectiveAnchorIndex =
        anchorIndex === -1 ? clickedIndex : anchorIndex;

    const start = Math.min(effectiveAnchorIndex, clickedIndex);
    const end = Math.max(effectiveAnchorIndex, clickedIndex);
    const range = new Set<number>(visualOrder.slice(start, end + 1));

    if (range.size < 2) {
        return EMPTY;
    }
    return range;
}

/**
 * Choose the active ROI after a bulk-set transition so the active-row highlight
 * never points outside a non-empty set. Keeps the current active ROI when it
 * survived, else lands on the just-clicked member (the shift endpoint), else the
 * first surviving member in visual order. Returns -1 for an empty set.
 */
export function reconcileActiveRoi(
    nextSet: ReadonlySet<number>,
    clicked: number,
    currentActive: number,
    visualOrder: readonly number[]
): number {
    if (nextSet.size === 0) {
        return -1;
    }
    if (nextSet.has(currentActive)) {
        return currentActive;
    }
    if (nextSet.has(clicked)) {
        return clicked;
    }
    for (const roiNumber of visualOrder) {
        if (nextSet.has(roiNumber)) {
            return roiNumber;
        }
    }
    // Every member is filtered out of the visual order; fall back to any.
    return nextSet.values().next().value ?? -1;
}

/**
 * Drop selected members that no longer exist. Returns the SAME reference when
 * nothing is pruned (so selection effects don't loop). Collapses to empty when
 * fewer than two members survive.
 */
export function pruneSelection(
    current: ReadonlySet<number>,
    existing: ReadonlySet<number>
): ReadonlySet<number> {
    let removed = false;
    const kept = new Set<number>();
    for (const roiNumber of current) {
        if (existing.has(roiNumber)) {
            kept.add(roiNumber);
        } else {
            removed = true;
        }
    }

    if (!removed) {
        return current;
    }
    if (kept.size < 2) {
        return EMPTY;
    }
    return kept;
}

/**
 * Toggle membership of `clicked` while multi-select MODE is armed.
 *
 * Unlike {@link applyCtrlToggle} this neither seeds from the active
 * structure nor collapses below two members: in mode the selection is
 * built up from nothing, one structure at a time, and every intermediate
 * size is a state the user is deliberately in. Seeding from the active
 * structure would tick a checkbox the user never ticked.
 */
export function applyModeToggle(
    current: ReadonlySet<number>,
    clicked: number
): ReadonlySet<number> {
    const working = new Set<number>(current);
    if (working.has(clicked)) {
        working.delete(clicked);
    } else {
        working.add(clicked);
    }
    return working;
}
